import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    X,
    Menu,
    LayoutGrid,
    FileArchive,
    Sparkles,
    MessageSquare,
    FileText,
    Settings,
    HelpCircle,
    ExternalLink,
    Bell,
    Plus,
    LogOut,
} from 'lucide-react';

import { getChildGraphAction, getClinics, getDoctorDetailsApi, getDoctorDashboardData, getLoanAmount, getParentGraphAction } from '../../api/action-creator';
import LoanTransactions from '../loanTransactions/LoanTransactions';
import Scans from '../scans/Scans';
import Overview from './Overview';
import Reports from './Reports';
import './dashboard.css'
import logo from "../../assets/images/carepayLogo.png";
const getInitials = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'CP';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const getFirstName = (name = '') => {
    const first = String(name).trim().split(/\s+/)[0];
    return first || 'there';
};

const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
};

const formatWorkspaceDate = () =>
    new Date().toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });

const getLastNDaysRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (Number(days) - 1));
    return {
        start: start.toISOString().split('T')[0],
        end: end.toISOString().split('T')[0],
    };
};

const getThisMonthRange = () => {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    return {
        start: start.toISOString().split('T')[0],
        end: end.toISOString().split('T')[0],
    };
};

const PAGE_COPY = {
    buisness: {
        title: 'Business overview',
        description: 'A clear view of your lending activity and partner performance.',
    },
    loan: {
        title: 'Loan transactions',
        description: 'Track latest application statuses and disbursals.',
    },
    scans: {
        title: 'Lumi AI',
        description: 'View user scan results, credits, and ledger activity.',
    },
    careena: {
        title: 'Careena',
        description: 'Open your Careena workspace for patient conversations.',
    },
    reports: {
        title: 'Reports',
        description: 'Download MIS reports for the selected date range.',
    },
    settings: {
        title: 'Settings',
        description: 'Your clinic QR code and account details.',
    },
};

// Dashboard Component
const Dashboard = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('disbursed');
    const [chartType, setChartType] = useState('count');
    const [sideTab, setSideTab] = useState("buisness"); // buisness, careena, loan, scans
    const [scansInitialTab, setScansInitialTab] = useState('patients');
    const [allClinicList, setAllClinicList] = useState([]);
    const [baseData, setBaseData] = useState([]);
    const [selectedClinic, setSelectedClinic] = useState("");
    const [doctorDetails, setDoctorDetails] = useState({});
    const [loanCountData, setLoanCountData] = useState({});
    const [dashboardData, setDashboardData] = useState({});
    const [, updateState] = useState();
    const forceUpdate = useCallback(() => updateState({}), []);

    // Mobile sidebar state — open by default on desktop
    const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
    );

    const initialDateRange = getThisMonthRange();
    const [startDate, setStartDate] = useState(initialDateRange.start);
    const [endDate, setEndDate] = useState(initialDateRange.end);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (location.state?.openScansCredits) {
            setSideTab('scans');
            setScansInitialTab('credits');
        }
    }, [location.state]);

    const getCurrentDatee = () => {
        return new Date().toISOString().split('T')[0];
    };

    const getMinDatee = () => {
        const date = new Date();
        date.setFullYear(date.getFullYear() - 1);
        return date.toISOString().split('T')[0];
    };

    const handleStartDateChange = (e) => {
        setStartDate(e.target.value);
        setErrors(prev => ({ ...prev, startDate: '', dateRange: '' }));
    };

    const handleEndDateChange = (e) => {
        setEndDate(e.target.value);
        setErrors(prev => ({ ...prev, endDate: '', dateRange: '' }));
    };

    const convertDateFormat=(dt)=>{
        if(dt === null || dt === ""){
            return "";
        }
        const date = dt;  // YYYY-MM-DD
        const [year, month, day] = date.split("-");
        const formatted = `${day}-${month}-${year}`;
        return formatted;
    }

    const handleApply = () => {
        const newErrors = {};

        if (!startDate) {
            newErrors.startDate = 'Start date is required';
        }

        if (!endDate) {
            newErrors.endDate = 'End date is required';
        }

        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            newErrors.dateRange = 'Start date cannot be later than end date';
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            console.log('Applying filters:', { selectedClinic, startDate, endDate });
            getLoanCount();
            getDashboardData();
            if (selectedClinic.length < 3) {
                getParentGraph();
            } else {
                getChildGraph();
            }
        }
    };

    const getLoanCount = (st,ed) => {
        getLoanAmount((selectedClinic.length > 1 ? selectedClinic : localStorage.getItem("doctorId")).toString(), convertDateFormat(st ?? startDate),convertDateFormat(ed ?? endDate),(callback) => {
            setLoanCountData(callback.data);
        }, (onErr) => { })
    }

    const getAllClinics = () => {
        getClinics((callback) => {
            if (callback.status === 200) {
                setAllClinicList(callback.data)
            }
        }, (onErr) => {
        });
    }

    const getDashboardData = (st, ed) => {
        const doctorId = (selectedClinic.length > 1 ? selectedClinic : localStorage.getItem("doctorId")).toString();
        const startDateParam = st !== undefined ? convertDateFormat(st) : (startDate ? convertDateFormat(startDate) : "");
        const endDateParam = ed !== undefined ? convertDateFormat(ed) : (endDate ? convertDateFormat(endDate) : "");
        
        getDoctorDashboardData(doctorId, startDateParam, endDateParam, (callback) => {
            if (callback.status === 200) {
                setDashboardData(callback.data);
            }
        }, (onErr) => {
            console.error('Error fetching dashboard data:', onErr);
        });
    }

    const getParentGraph = (st,ed) => {
        getParentGraphAction(chartType === "money" ? "amount" : chartType, activeTab, convertDateFormat(st ?? startDate),convertDateFormat(ed ?? endDate), (callback) => {
            if (callback.status === 200) {
                setBaseData(callback.data)
            }
        }, (onErr) => { });
    }

    const getChildGraph = (st,ed) => {
        getChildGraphAction(selectedClinic, chartType === "money" ? "amount" : chartType, activeTab,  convertDateFormat(st ?? startDate),convertDateFormat(ed ?? endDate),(callback) => {
            if (callback.status === 200) {
                setBaseData(callback.data)
            }
        }, (onErr) => { });
    }

    const getDoctorDetails = () => {
        getDoctorDetailsApi((callback) => {
            setDoctorDetails(callback.data)
        })
    }

    useEffect(() => {
        getLoanCount();
        getAllClinics();
        getParentGraph();
        getDoctorDetails();
        getDashboardData();
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (selectedClinic.length < 3) {
            getParentGraph();
        } else {
            getChildGraph();
        }
    }, [activeTab, chartType]); // eslint-disable-line react-hooks/exhaustive-deps

    const applyDatePreset = (presetId) => {
        const range =
            presetId === 'month' ? getThisMonthRange() : getLastNDaysRange(Number(presetId));
        setStartDate(range.start);
        setEndDate(range.end);
        setErrors({});
    };

    const handleReset=()=>{
        const range = getThisMonthRange();
        setStartDate(range.start);
        setEndDate(range.end);
        setSelectedClinic("");
        setErrors({});
        forceUpdate();
        getLoanCount(range.start, range.end);
        getDashboardData(range.start, range.end);
        getParentGraph(range.start, range.end);
    }

    const toggleSidebar = () => {
        setIsSidebarOpen((prev) => !prev);
    };

    const closeSidebar = () => {
        setIsSidebarOpen(false);
    };

    const getCareenaUrl = () => {
        const merchantCode = localStorage.getItem('merchantCode') || '';
        const password = localStorage.getItem('password') || '';
        return `https://loanbot.carepay.money/doctor-login?merchantCode=${encodeURIComponent(merchantCode)}&password=${encodeURIComponent(password)}`;
    };

    const openCareenaInNewTab = () => {
        window.open(getCareenaUrl(), '_blank', 'noopener,noreferrer');
        if (typeof window !== 'undefined' && window.innerWidth < 1024) {
            closeSidebar();
        }
    };

    const handleNavItemClick = (tab) => {
        setSideTab(tab);
        // On smaller screens, close drawer after picking a page
        if (typeof window !== 'undefined' && window.innerWidth < 1024) {
            closeSidebar();
        }
    };

    useEffect(() => {
        const isMobileDrawer = typeof window !== 'undefined' && window.innerWidth < 1024;
        if (isSidebarOpen && isMobileDrawer) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isSidebarOpen]);

    const firstName = getFirstName(doctorDetails?.name);
    const pageCopy = PAGE_COPY[sideTab] || PAGE_COPY.buisness;

    return (
        <div className={`dashboard-container ${isSidebarOpen ? 'menu-open' : 'menu-closed'}`}>
            <nav className="dashboard-header">
                <div className="header-content">
                    <div className="header-left">
                        <button
                            type="button"
                            className="header-icon-btn"
                            onClick={toggleSidebar}
                            aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={isSidebarOpen}
                        >
                            {isSidebarOpen ? (
                                <X size={18} strokeWidth={2.25} />
                            ) : (
                                <Menu size={18} strokeWidth={2.25} />
                            )}
                        </button>
                        <span className="carepay-wordmark" aria-label="CarePay">
                            {/* Care<span>Pay</span> */}
                            <img src={logo} alt="CarePay" style={{ width: '120px' }} />
                        </span>
                    </div>
                    <button type="button" className="header-bell-btn" aria-label="Notifications">
                        <Bell size={18} strokeWidth={1.75} />
                    </button>
                </div>
            </nav>

            {/* Sidebar Overlay — mobile/tablet only */}
            <div
                className={`sidebar-overlay ${isSidebarOpen ? 'open' : ''}`}
                onClick={closeSidebar}
                aria-hidden={!isSidebarOpen}
            />

            {/* Sidebar */}
            <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-content">
                    <div className="user-profile-section">
                        <div className="user-avatar-large">
                            {getInitials(doctorDetails?.name)}
                        </div>
                        <div className="user-info-large">
                            <div className="user-name-large">
                                {doctorDetails?.name ?? "User Name"}
                            </div>
                            <div className="user-clinic-large">
                                {doctorDetails?.clinicName ?? "Clinic Name"}
                            </div>
                        </div>
                    </div>

                    <div className="sidebar-nav-scroll">
                        <div className="sidebar-nav-group">
                            <p className="sidebar-nav-heading">Workspace</p>
                            <button
                                type="button"
                                className={`nav-item ${sideTab === "buisness" ? "active" : ""}`}
                                onClick={() => handleNavItemClick("buisness")}
                            >
                                <LayoutGrid size={18} strokeWidth={1.75} />
                                <span className="nav-label">Overview</span>
                            </button>
                            <button
                                type="button"
                                className={`nav-item ${sideTab === "loan" ? "active" : ""}`}
                                onClick={() => handleNavItemClick("loan")}
                            >
                                <FileArchive size={18} strokeWidth={1.75} />
                                <span className="nav-label">Loan transactions</span>
                            </button>
                            <button
                                type="button"
                                className={`nav-item ${sideTab === "scans" ? "active" : ""}`}
                                onClick={() => handleNavItemClick("scans")}
                            >
                                <Sparkles size={18} strokeWidth={1.75} />
                                <span className="nav-label">Lumi AI</span>
                            </button>
                            <button
                                type="button"
                                className="nav-item"
                                onClick={openCareenaInNewTab}
                            >
                                <MessageSquare size={18} strokeWidth={1.75} />
                                <span className="nav-label">Careena</span>
                                <ExternalLink size={14} strokeWidth={1.75} className="nav-external-icon" />
                            </button>
                        </div>

                        <div className="sidebar-nav-group">
                            <p className="sidebar-nav-heading">Manage</p>
                            <button
                                type="button"
                                className={`nav-item ${sideTab === "reports" ? "active" : ""}`}
                                onClick={() => handleNavItemClick("reports")}
                            >
                                <FileText size={18} strokeWidth={1.75} />
                                <span className="nav-label">Reports</span>
                            </button>
                            {/* <button
                                type="button"
                                className={`nav-item ${sideTab === "settings" ? "active" : ""}`}
                                onClick={() => handleNavItemClick("settings")}
                            >
                                <Settings size={18} strokeWidth={1.75} />
                                <span className="nav-label">Settings</span>
                            </button> */}
                        </div>
                    </div>

                    <div className="sidebar-footer">
                        <a
                            className="sidebar-help-card"
                            href="https://carepay.money"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <span className="sidebar-help-icon">
                                <HelpCircle size={18} strokeWidth={1.75} />
                            </span>
                            <span className="sidebar-help-copy">
                                <strong>Need help?</strong>
                                <span>Talk to our partner team</span>
                            </span>
                            <ExternalLink size={16} strokeWidth={1.75} />
                        </a>

                        <button
                            type="button"
                            className="logout-item"
                            onClick={() => {
                                localStorage.clear();
                                sessionStorage.clear();
                                navigate('/', { replace: true });
                            }}
                        >
                            <LogOut size={18} strokeWidth={1.75} />
                            <span className="nav-label">Sign out</span>
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <div className={`main-content ${isSidebarOpen ? 'sidebar-open' : ''}`}>
                {sideTab !== 'loan' && sideTab !== 'reports' && sideTab !== 'settings' && (
                    <>
                {/* <header className="workspace-header">
                    <div>
                        <p className="workspace-kicker">Partner workspace</p>
                        <h1 className="workspace-greeting">
                            {getGreeting()}, {firstName}
                        </h1>
                    </div>
                </header> */}

                    <div className="page-heading">
                        {/* <p className="page-heading-date">{formatWorkspaceDate()}</p> */}
                        <div className="page-heading-row">
                            <div>
                                <h2 className="page-heading-title">{pageCopy.title}</h2>
                                <p className="page-heading-desc">{pageCopy.description}</p>
                            </div>
                            {sideTab === 'buisness' && (
                                <button
                                    type="button"
                                    className="page-heading-cta"
                                    onClick={openCareenaInNewTab}
                                >
                                    <Plus size={16} strokeWidth={2.25} />
                                    New transaction
                                </button>
                            )}
                        </div>
                    </div>
                    </>
                )}

                {sideTab === "buisness" ? (
                    <Overview
                        loanCountData={loanCountData}
                        dashboardData={dashboardData}
                        allClinicList={allClinicList}
                        selectedClinic={selectedClinic}
                        onClinicChange={setSelectedClinic}
                        startDate={startDate}
                        endDate={endDate}
                        onStartDateChange={handleStartDateChange}
                        onEndDateChange={handleEndDateChange}
                        minDate={getMinDatee()}
                        maxDate={getCurrentDatee()}
                        errors={errors}
                        onApply={handleApply}
                        onReset={handleReset}
                        onApplyPreset={applyDatePreset}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                        chartType={chartType}
                        onChartTypeChange={setChartType}
                        chartData={getChartData()}
                        maxValue={getChartData().length > 0 ? Math.max(...getChartData().map((d) => d.value)) : 0}
                        onReviewQueue={() => handleNavItemClick('loan')}
                    />
                ) : sideTab === "loan" ? (
                    <LoanTransactions />
                ) : sideTab === "scans" ? (
                    <Scans initialTab={scansInitialTab} />
                ) : sideTab === "reports" ? (
                    <Reports
                        clinics={allClinicList}
                        minDate={getMinDatee()}
                        maxDate={getCurrentDatee()}
                    />
                )
                //  : sideTab === "settings" ? (
                //     <div className="coming-soon-page">
                //         <span className="coming-soon-icon">
                //             <Settings size={28} strokeWidth={1.75} />
                //         </span>
                //         <h1>Settings</h1>
                //         <p>Coming soon</p>
                //     </div>
                // ) 
                : null}
            </div>
        </div>
    );

    // Helper function for chart data
    function getChartData() {
        if (!Array.isArray(baseData)) return [];
        return baseData.slice(0, 6).reverse().map(item => ({
            month: `${item.month ?? ""} ${item.year.toString().slice(2, 4) ?? ""}`,
            value: Number(item.value ?? 0)
        }));
    }
};

export default Dashboard;
